import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, MessageCircle, Send, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useChat } from '@/modules/farms/hooks/useChat';
import { useChatThreads, threadTitle, type ChatThreadSummary } from '@/modules/farms/hooks/useChatThreads';
import { formatTime } from '@/lib/format';

/**
 * Chat flutuante (estilo Messenger): ícone redondo fixo no canto inferior
 * direito. Ao clicar, abre a lista de conversas; ao escolher uma, a própria
 * janela vira o chat (ler + enviar), com voltar para a lista.
 */

// ─── Conversa aberta dentro da janela ─────────────────────────────────────────

function MiniChat({ thread, onBack }: { thread: ChatThreadSummary; onBack: () => void }) {
  const { user } = useAuth();
  const { messages, loading, sending, sendError, sendMessage, markRead } = useChat(thread.thread_id);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    void markRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  async function handleSend() {
    const content = input.trim();
    if (!content || sending) return;
    const ok = await sendMessage(content);
    if (ok) setInput('');
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header da conversa */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border shrink-0">
        <button onClick={onBack} className="text-ink-muted hover:text-ink" title="Voltar às conversas">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <p className="text-sm font-semibold text-ink truncate flex-1">{threadTitle(thread, user?.id)}</p>
      </div>

      {/* Mensagens */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {loading && <p className="text-xs text-ink-muted text-center py-4">Carregando…</p>}
        {!loading && messages.length === 0 && (
          <p className="text-xs text-ink-muted text-center py-4">Nenhuma mensagem ainda. Diga olá!</p>
        )}
        {messages.map((msg) => {
          const isMe = msg.sender_user_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 ${
                isMe ? 'bg-brand-700 text-white rounded-tr-sm' : 'bg-surface-subtle text-ink rounded-tl-sm'
              }`}>
                <p className="text-xs whitespace-pre-wrap break-words">{msg.content}</p>
                <p className={`text-[10px] mt-0.5 ${isMe ? 'text-brand-200' : 'text-ink-soft'}`}>
                  {formatTime(msg.created_at)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      {sendError && <p className="text-[11px] text-negative px-3 pb-1">{sendError}</p>}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-border shrink-0">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); } }}
          placeholder="Escreva uma mensagem…"
          className="flex-1 text-sm bg-surface-subtle rounded-full px-3.5 py-2 outline-none text-ink placeholder:text-ink-muted"
        />
        <button
          onClick={() => void handleSend()}
          disabled={sending || !input.trim()}
          className="w-9 h-9 rounded-full bg-brand-700 text-white flex items-center justify-center disabled:opacity-40 shrink-0"
          title="Enviar"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── FAB + janela ──────────────────────────────────────────────────────────────

export function ChatFab() {
  const { user } = useAuth();
  const { threads, totalUnread } = useChatThreads();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<ChatThreadSummary | null>(null);

  if (!user) return null;

  // Mantém o thread ativo atualizado quando a lista recarrega
  const activeThread = active ? threads.find((t) => t.thread_id === active.thread_id) ?? active : null;

  return (
    <>
      {/* Janela */}
      {open && (
        <div
          className="fixed bottom-24 right-5 z-40 w-80 card shadow-2xl overflow-hidden flex flex-col"
          style={{ height: 420 }}
        >
          {activeThread ? (
            <MiniChat thread={activeThread} onBack={() => setActive(null)} />
          ) : (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
                <p className="text-sm font-bold text-ink">Conversas</p>
                <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {threads.length === 0 ? (
                  <p className="text-xs text-ink-muted text-center px-6 py-10">
                    Nenhuma conversa ainda. Ao contatar uma farm (ou receber uma mensagem),
                    ela aparecerá aqui.
                  </p>
                ) : (
                  threads.map((t) => (
                    <button
                      key={t.thread_id}
                      onClick={() => setActive(t)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-subtle transition-colors text-left"
                    >
                      <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
                        <MessageCircle className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${t.unread_count > 0 ? 'font-bold text-ink' : 'font-medium text-ink'}`}>
                          {threadTitle(t, user.id)}
                        </p>
                        <p className={`text-xs truncate ${t.unread_count > 0 ? 'text-ink font-medium' : 'text-ink-muted'}`}>
                          {t.last_message
                            ? `${t.last_sender_id === user.id ? 'Você: ' : ''}${t.last_message}`
                            : 'Sem mensagens'}
                        </p>
                      </div>
                      {t.unread_count > 0 && (
                        <span className="w-5 h-5 rounded-full bg-brand-700 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                          {t.unread_count > 9 ? '9+' : t.unread_count}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Ícone redondo fixo */}
      <button
        onClick={() => { setOpen((v) => !v); if (open) setActive(null); }}
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-brand-700 hover:bg-brand-800 text-white shadow-xl flex items-center justify-center transition-colors"
        title={open ? 'Fechar conversas' : 'Abrir conversas'}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-negative text-white text-[11px] font-bold flex items-center justify-center border-2 border-white">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>
    </>
  );
}
